// The wave engine from fb/web: three wandering waves and slow noise on top of a Catmull-Rom curve
// through the anchors. Calm at the left edge, freer to the right, and the amplitude breathes.

/** t = seconds × SPEED. */
export const SPEED = 0.75;
/** Multiplier on the amplitude. */
export const SWELL = 2.5;

export type RibbonWave = {
  /** Width of the viewBox. */
  W: number;
  viewBox: string;
  /** Anchors in viewBox coordinates, left to right. */
  base: [number, number][];
  amp: number;
  seed: number;
};

// Follows the static "settle" curve: down the right side to the button row of a page top.
const SETTLE: [number, number][] = [
  [-40, 300],
  [300, 170],
  [640, 190],
  [900, 360],
  [1100, 560],
  [1300, 760],
  [1500, 800],
];

export const WAVES = {
  hero: {
    W: 1440,
    viewBox: "0 0 1440 900",
    base: [
      [-40, 760],
      [220, 720],
      [400, 790],
      [560, 740],
      [760, 640],
      [920, 575],
      [1040, 600],
      [1200, 520],
      [1340, 430],
      [1500, 330],
    ],
    amp: 58,
    seed: 1.7,
  },
  settle: { W: 1440, viewBox: "0 0 1440 900", base: SETTLE, amp: 46, seed: 3.1 },
  // The same shape as the survey top with its own seed, so the two never move in step.
  settleGoal: { W: 1440, viewBox: "0 0 1440 900", base: SETTLE, amp: 46, seed: 5.6 },
} satisfies Record<string, RibbonWave>;

export type WaveName = keyof typeof WAVES;

/** Smooth 1D value noise in [-1, 1]. */
export const noise = (x: number) => {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f);
  const h = (n: number) => {
    const v = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return v - Math.floor(v);
  };
  return (h(i) * (1 - u) + h(i + 1) * u) * 2 - 1;
};

/** Catmull-Rom through the anchors. */
export const baseY = (base: [number, number][], x: number) => {
  const n = base.length;
  let i = 0;
  while (i < n - 2 && x > base[i + 1][0]) i++;
  const p0 = base[Math.max(0, i - 1)][1];
  const p1 = base[i][1];
  const p2 = base[i + 1][1];
  const p3 = base[Math.min(n - 1, i + 2)][1];
  const u = Math.max(0, Math.min(1, (x - base[i][0]) / (base[i + 1][0] - base[i][0])));
  return 0.5 * (2 * p1 + (-p0 + p2) * u + (2 * p0 - 5 * p1 + 4 * p2 - p3) * u * u + (-p0 + 3 * p1 - 3 * p2 + p3) * u * u * u);
};

export const wave = (r: RibbonWave, x: number, t: number, lag: number) => {
  const s = r.seed;
  const u = (x + 40) / (r.W + 80);
  const env = 0.35 + 0.65 * Math.sin(Math.PI * Math.min(1, u * 1.15)); // calm at the edges
  const breathe = 0.75 + 0.35 * noise(t * 0.07 + s); // slow, uneven pulse
  const tt = t + lag;
  const y =
    0.55 * Math.sin(u * 5.3 - tt * 0.21 + s) +
    0.3 * Math.sin(u * 8.9 + tt * 0.13 + s * 2.1) +
    0.18 * Math.sin(u * 14.7 - tt * 0.34 + s * 0.7) +
    0.22 * noise(u * 3.1 + tt * 0.05 + s * 5);
  return r.amp * SWELL * env * breathe * y;
};

const N = 36;
const f = (v: number) => v.toFixed(1);

/** The band at time t as an svg path, moved dy down and lag behind. */
export const pathFor = (r: RibbonWave, t: number, dy: number, lag: number) => {
  const pts: [number, number][] = [];
  const x0 = r.base[0][0];
  const x1 = r.base[r.base.length - 1][0];
  for (let i = 0; i <= N; i++) {
    const x = x0 + ((x1 - x0) * i) / N;
    pts.push([x, baseY(r.base, x) + dy + wave(r, x, t, lag)]);
  }
  let d = `M ${f(pts[0][0])} ${f(pts[0][1])}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${f(c1x)} ${f(c1y)}, ${f(c2x)} ${f(c2y)}, ${f(p2[0])} ${f(p2[1])}`;
  }
  return d;
};

/** The main line (blurred layer and tail) and the wide shadow line that lags a little behind. */
export const framePaths = (r: RibbonWave, t: number) => ({ main: pathFor(r, t, 0, 0), shadow: pathFor(r, t, 36, -2.5) });
